import { __assign, __awaiter, __generator, __read, __rest } from "tslib";
import React, { useRef, useState, useEffect } from "react";
import classNames from "classnames";
import { useTranslation } from "../i18n";
import { SearchBox } from "../searchbox";
import { Dropdown } from "../dropdown";
import { useDefaultValue } from "../form";
import { VirtualizedList } from "../list/VirtualizedList";
import { Text } from "../text";
import { EmptyTip } from "../tips";
import { Button } from "../button";
import { KeyMap } from "../_util/key-map";
import { injectValue } from "../_util/inject-value";
import { useDefault } from "../_util/use-default";
import { noop } from "../_util/noop";
import { warn } from "../_util/warn";
import { useConfig } from "../_util/config-context";
import { searchFilter } from "../_util/search-filter";
import { mergeRefs } from "../util";
import { isMobile } from "../_util/is-mobile";
import { useUnmounted } from "../_util/use-unmounted";
import { getListItems } from "./util";
var defaultFilter = function (inputValue, _a) {
    var text = _a.text, value = _a.value;
    var optionText = String(typeof text === "string" ? text : value);
    return searchFilter(optionText, inputValue);
};
var trueFilter = function () { return true; };
/**
 * 模拟 Select
 */
export var SimulateSelect = React.forwardRef(function SimulateSelect(props, ref) {
    var _a;
    var _this = this;
    var t = useTranslation();
    var classPrefix = useConfig().classPrefix;
    var _b = useDefaultValue(props, null), _c = _b.virtual, virtual = _c === void 0 ? true : _c, value = _b.value, onChange = _b.onChange, _d = _b.options, options = _d === void 0 ? [] : _d, groups = _b.groups, _e = _b.placeholder, placeholder = _e === void 0 ? t.pleaseSelect : _e, button = _b.button, size = _b.size, disabled = _b.disabled, onScrollBottom = _b.onScrollBottom, listHeight = _b.listHeight, listWidth = _b.listWidth, bottomTips = _b.bottomTips, _f = _b.boxStyle, boxStyle = _f === void 0 ? {} : _f, _g = _b.onOpen, onOpen = _g === void 0 ? noop : _g, _h = _b.onClose, onClose = _h === void 0 ? noop : _h, searchable = _b.searchable, _j = _b.searchPlaceholder, searchPlaceholder = _j === void 0 ? "" : _j, _k = _b.onSearch, onSearch = _k === void 0 ? noop : _k, _l = _b.filter, customizeFilter = _l === void 0 ? defaultFilter : _l, filterRender = _b.filterRender, _m = _b.defaultSearchValue, defaultSearchValue = _m === void 0 ? "" : _m, searchValue = _b.searchValue, onSearchValueChange = _b.onSearchValueChange, _o = _b.autoClearSearchValue, autoClearSearchValue = _o === void 0 ? true : _o, clearable = _b.clearable, refreshable = _b.refreshable, _p = _b.onRefresh, onRefresh = _p === void 0 ? noop : _p, footer = _b.footer, _q = _b.appearance, appearance = _q === void 0 ? props.appearence : _q, // eslint-disable-line react/destructuring-assignment
    dropdownProps = __rest(_b, ["virtual", "value", "onChange", "options", "groups", "placeholder", "button", "size", "disabled", "onScrollBottom", "listHeight", "listWidth", "bottomTips", "boxStyle", "onOpen", "onClose", "searchable", "searchPlaceholder", "onSearch", "filter", "filterRender", "defaultSearchValue", "searchValue", "onSearchValueChange", "autoClearSearchValue", "clearable", "refreshable", "onRefresh", "footer", "appearance"]);
    // 移动端 focus 可能产生滚动导致关闭
    if (isMobile && searchable) {
        dropdownProps.closeOnScroll = false;
    }
    var _r = __read(useState(false), 2), refreshing = _r[0], setRefreshing = _r[1];
    var dropdownRef = useRef(null);
    var vListRef = useRef(null);
    var InputRef = useRef(null);
    var _s = __read(useDefault(searchValue, defaultSearchValue, onSearchValueChange), 2), inputValue = _s[0], setInputValue = _s[1];
    var unmountedRef = useUnmounted();
    var filter = searchable ? customizeFilter : trueFilter;
    function focus() {
        if (!isMobile && searchable) {
            setTimeout(function () {
                if (InputRef.current) {
                    InputRef.current.focus();
                }
            }, 100); // 第一次展开时 Input 还未渲染
        }
    }
    var selected = value != null ? options.find(function (x) { return x.value === value; }) : undefined;
    // 选项消失不影响已选中值显示
    var _t = __read(useState(function () {
        if (!selected) {
            return undefined;
        }
        return selected;
    }), 2), buttonOption = _t[0], setButtonOption = _t[1];
    // options 中有选中项时改变 buttonOption
    useEffect(function () {
        if (selected) {
            setButtonOption(selected);
        }
    }, [options]); // eslint-disable-line react-hooks/exhaustive-deps
    // value 变化时改变 buttonOption 及搜索框状态
    useEffect(function () {
        if (selected) {
            setButtonOption(selected);
        }
        else {
            setButtonOption(undefined);
        }
    }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(function () {
        if (options.length > 10 && !searchable) {
            warn("Select 中 `options` 的项数较多，建议开启搜索功能");
        }
    }, [options, searchable]);
    var buttonPlaceholder = placeholder;
    if (!appearance || appearance === "button" || appearance === "default") {
        buttonPlaceholder = React.createElement(Text, { theme: "weak" }, placeholder);
    }
    // 按钮文字
    var buttonText = 
    // eslint-disable-next-line no-nested-ternary
    buttonOption
        ? typeof buttonOption.text === "undefined"
            ? buttonOption.value
            : buttonOption.text
        : buttonPlaceholder || t.pleaseSelect;
    var hasGroup = !!options.find(function (opt) { return !!opt.groupKey; }) && groups;
    // 筛选
    var filteredOptions = options.filter(function (options) {
        return filter(inputValue, options);
    });
    // 提示
    var tips = props.tips;
    if (typeof tips === "undefined" && filteredOptions.length === 0) {
        tips = React.createElement(EmptyTip, null);
    }
    var items = getListItems({
        tips: injectValue(tips)(filteredOptions),
        bottomTips: injectValue(bottomTips)(filteredOptions),
        groups: groups,
        options: filteredOptions,
    }).map(function (item) {
        if (item.type === "option" && searchable && filterRender) {
            return __assign(__assign({}, item), { text: filterRender(inputValue, item.option) });
        }
        return item;
    });
    var selectedIndex = items.findIndex(function (x) { return x.type === "option" && x.option.value === value; });
    var _u = __read(useState(-1), 2), currentIndex = _u[0], setCurrentIndex = _u[1];
    useEffect(function () {
        setCurrentIndex(selectedIndex);
    }, [selectedIndex]);
    var count = items.length;
    function handleKeyDown(source, event, 
    // input 托管事件时没有 context，此时一定为 open 状态
    context, close) {
        if (context === void 0) { context = { open: true }; }
        if (close === void 0) { close = function () { return null; }; }
        var getOptionIndex = function (current, step) {
            var _a;
            if (step === void 0) { step = 1; }
            var flag = 1;
            var index = (current + step + count) % count;
            var item = items[index % count];
            while (flag < count &&
                (item.type !== "option" || ((_a = item.option) === null || _a === void 0 ? void 0 : _a.disabled))) {
                flag += 1;
                index = (index + step + count) % count;
                item = items[index % count];
            }
            return index;
        };
        var ok = function () {
            var item = items[currentIndex % count];
            if (item && item.type === "option" && context.open) {
                // 输入时不能使用空格选中
                if (item.option.disabled) {
                    return false;
                }
                onChange(item.option.value, { event: event, option: item.option });
                close();
            }
            return true;
        };
        switch (event.key) {
            case KeyMap.Space:
                if (source === "input") {
                    return false;
                }
                event.preventDefault();
                return ok();
            case KeyMap.Enter:
                return ok();
            case KeyMap.Up:
                event.preventDefault();
                if (count && context.open) {
                    setCurrentIndex(function (c) {
                        var index = getOptionIndex(c, -1);
                        if (vListRef.current) {
                            vListRef.current.scrollToItem(index);
                        }
                        return index;
                    });
                }
                break;
            case KeyMap.Down:
                event.preventDefault();
                if (count && context.open) {
                    setCurrentIndex(function (c) {
                        var index = getOptionIndex(c);
                        if (vListRef.current) {
                            vListRef.current.scrollToItem(index);
                        }
                        return index;
                    });
                }
                break;
            case KeyMap.Esc:
                close();
                break;
        }
        return true;
    }
    if (listWidth) {
        boxStyle.width = listWidth;
        boxStyle.minWidth = listWidth;
    }
    var select = (React.createElement(Dropdown, __assign({ updateOnChildrenChange: true, preventMouseDown: !searchable && !footer, ref: mergeRefs(ref, dropdownRef), clickClose: false, size: size, boxStyle: boxStyle, appearance: appearance, footer: footer, button: typeof button !== "undefined"
            ? injectValue(button)(buttonOption)
            : buttonText, onOpen: function () {
            onOpen();
            focus();
        }, onClose: function () {
            onClose();
            // 已经未空值不再次置回初始
            if (autoClearSearchValue) {
                setTimeout(function () {
                    if (!unmountedRef.current) {
                        setInputValue("", {});
                    }
                }, 100);
            }
        }, onKeyDown: function (event, context) { return handleKeyDown("dropdown", event, context); }, clearable: value != null && clearable, onClear: function (event) {
            onChange(null, { event: event, option: null });
            setButtonOption(null);
        }, disabled: disabled || refreshing }, dropdownProps), function (close) { return (React.createElement(React.Fragment, null,
        searchable && (React.createElement(SearchBox, { simple: true, ref: InputRef, value: inputValue, onChange: function (value, context) {
                setInputValue(value, context);
                onSearch(value, context);
            }, onClear: focus, placeholder: searchPlaceholder, onKeyDown: function (event) {
                return handleKeyDown("input", event, undefined, close);
            }, onSearch: onSearch })),
        React.createElement(VirtualizedList, { virtual: virtual, containerStyle: boxStyle, containerClassName: dropdownProps.boxClassName, items: items.map(function (item, index) {
                if (item.type === "option") {
                    var option_1 = item.option;
                    return __assign(__assign({}, item), { props: {
                            disabled: option_1.disabled,
                            selected: option_1 === selected,
                            current: currentIndex === index,
                            onClick: function (event) {
                                onChange(option_1.value, { event: event, option: option_1 });
                                close();
                            },
                            tooltip: option_1.tooltip,
                            className: hasGroup && !option_1.groupKey
                                ? classPrefix + "-list__item--single"
                                : "",
                            "aria-selected": option_1 === selected,
                        } });
                }
                return item;
            }), virtualizedRef: vListRef, type: hasGroup ? "option-group" : "option", onScrollBottom: onScrollBottom, listHeight: listHeight, initialScrollOffsetIndex: selectedIndex }))); }));
    if (refreshable) {
        return (React.createElement("div", { className: classNames(classPrefix + "-select-refresh", (_a = {},
                _a[classPrefix + "-select-refresh--borderless"] = appearance !== "button",
                _a)), style: size === "full" ? { display: "block" } : undefined },
            select,
            React.createElement(Button, { type: "icon", icon: "refresh", htmlType: "button", className: classNames({ "is-loading": refreshing }), disabled: disabled || refreshing, onClick: function () { return __awaiter(_this, void 0, void 0, function () {
                    return __generator(this, function (_a) {
                        switch (_a.label) {
                            case 0:
                                setRefreshing(true);
                                return [4 /*yield*/, onRefresh()];
                            case 1:
                                _a.sent();
                                setRefreshing(false);
                                return [2 /*return*/];
                        }
                    });
                }); } })));
    }
    return select;
});
SimulateSelect.displayName = "SimulateSelect";
//# sourceMappingURL=SimulateSelect.js.map